import React from 'react';

import { getCrmRecordCopy } from '@aline/common';
import { link } from '@aline/routes';
import { IconButton, Typography } from '@mui/material';
import type { GridCellParams } from '@mui/x-data-grid-premium';
import { Eye } from 'lucide-react';
import { Link } from 'react-router-dom';
import { AlineTooltip } from 'src/components/generic';
import { getWorkflowRuntimeString } from 'src/components/workflow/workflow-helpers';
import type { WorkflowListGridRow, WorkflowListRow } from 'src/components/workflow/workflow-list';
import {
  getIsWorkflowListWorkflowRow,
  getWorkflowIdFromListRow,
} from 'src/components/workflow/workflow-list';
import {
  PriorityDisplay,
  WorkflowPriority,
} from 'src/components/workflow/workflow-list/cells/WorkflowPriority';
import { Apollo } from 'src/gql/generated';
import { formatDateForDisplay, openLinkFromAnchorClick } from 'src/helpers';
import { INTEGRATION_DISPLAY_NAME } from 'src/helpers/integration-display-name';
import { CrmIntegrationIcon } from 'src/images/integrations/CrmIntegrationIcon';
import { createClampStyles } from 'src/theme';
import { makeStyles } from 'tss-react/mui';

const useStyles = makeStyles()(theme => ({
  button: {
    visibility: 'hidden' as const,
  },
  cell: {
    alignItems: 'center',
    display: 'flex',
    flexWrap: 'nowrap' as const,
    gap: theme.spacing(0.5),
    minWidth: 0,
    width: '100%',
  },
  crmLink: {
    alignItems: 'center',
    cursor: 'default',
    display: 'flex',
    flexShrink: 0,
  },
  crmLinkIcon: {
    display: 'block',
    height: 14,
    objectFit: 'contain',
    width: 14,
  },
  crmLinkMissing: {
    filter: 'grayscale(1)',
    opacity: 0.45,
  },
  crmLinks: {
    alignItems: 'center',
    display: 'flex',
    flexShrink: 0,
    gap: theme.spacing(0.25),
  },
  metadataRow: {
    alignItems: 'center',
    display: 'flex',
    flexWrap: 'nowrap',
    gap: theme.spacing(0.75),
    minWidth: 0,
    overflow: 'hidden',
  },
  nameLink: {
    color: 'inherit',
    minWidth: 0,
    textDecoration: 'none',
  },
  nameRow: {
    alignItems: 'center',
    display: 'flex',
    flexWrap: 'nowrap',
    gap: theme.spacing(0.5),
    minWidth: 0,
    width: '100%',
  },
  runtime: {
    color: theme.palette.text.secondary,
    flexShrink: 0,
    fontSize: theme.typography.pxToRem(12),
  },
  title: {
    ...createClampStyles(3),
    fontWeight: 600,
  },
  workflowNameContainer: {
    alignItems: 'start',
    display: 'flex',
    flexDirection: 'column' as const,
    gap: theme.spacing(0.25),
    minWidth: 0,
    width: '100%',
  },
}));

export function WorkflowListTitleCell({
  row,
  value,
}: GridCellParams<WorkflowListGridRow, WorkflowListRow['name']>): JSX.Element | null {
  const { classes } = useStyles();
  const [updateWorkflow] = Apollo.useUpdateWorkflowMutation();
  if (!getIsWorkflowListWorkflowRow(row)) return null;

  const workflowName = value || 'Untitled Workflow';
  const startedAt = row.startedAt ?? row.createdAt;
  const runtimeLabel = `Runtime: ${getWorkflowRuntimeString(row)}`;
  const workflowHref = link.WorkflowDetailTabSummary({ workflowId: getWorkflowIdFromListRow(row) });

  return (
    <div className={classes.cell}>
      <div className={classes.workflowNameContainer}>
        <div className={classes.nameRow}>
          <AlineTooltip arrow enterDelay={500} placement="top-start" title={workflowName}>
            <Link
              className={classes.nameLink}
              to={workflowHref}
              onClick={e => openLinkFromAnchorClick(e, workflowHref, { title: workflowName })}
            >
              <Typography className={classes.title} variant="subtitle1">
                {workflowName}
              </Typography>
            </Link>
          </AlineTooltip>
          <WorkflowListCrmLinkIcon linkedOpportunities={row.linkedOpportunities} />
        </div>
        <div className={classes.metadataRow}>
          <WorkflowPriority
            priority={row.priority}
            renderTrigger={priority => <PriorityDisplay priority={priority} />}
            onPriorityChange={async priority => {
              await updateWorkflow({
                variables: {
                  input: {
                    props: { priority },
                    workflowId: getWorkflowIdFromListRow(row),
                  },
                },
              });
            }}
          />
          <AlineTooltip arrow placement="top" title={`Started ${formatDateForDisplay(startedAt)}`}>
            <Typography noWrap className={classes.runtime} variant="body2">
              {runtimeLabel}
            </Typography>
          </AlineTooltip>
        </div>
      </div>
    </div>
  );
}

interface QuickViewButtonProps extends React.HTMLAttributes<HTMLElement> {
  onClick: (e: React.MouseEvent<HTMLButtonElement>) => void;
  tooltipMsg?: string;
}

export function QuickViewButton({
  id,
  onClick,
  tooltipMsg = 'Quick view',
  ...rest
}: QuickViewButtonProps): JSX.Element {
  const { classes } = useStyles();

  return (
    <AlineTooltip arrow placement="top" title={tooltipMsg}>
      <div {...rest}>
        <IconButton className={classes.button} id={id} size="small" onClick={onClick}>
          <Eye />
        </IconButton>
      </div>
    </AlineTooltip>
  );
}

type CrmLinkSummary = { count: number; missing: boolean; names: string[] };

/**
 * One mark per CRM the workflow is linked to, whatever the number of records in it. A row
 * hydrated from the Apollo cache can arrive without the links, hence the undefined case.
 */
function WorkflowListCrmLinkIcon({
  linkedOpportunities,
}: {
  linkedOpportunities:
    | Pick<Apollo.LinkedOpportunity, 'integration' | 'opportunityName' | 'recordMissingAt'>[]
    | undefined;
}): JSX.Element | null {
  const { classes, cx } = useStyles();
  const linksByIntegration = new Map<Apollo.Integration, CrmLinkSummary>();
  for (const { integration, opportunityName, recordMissingAt } of linkedOpportunities ?? []) {
    const links = linksByIntegration.get(integration) ?? { count: 0, missing: false, names: [] };
    links.count += 1;
    links.missing ||= Boolean(recordMissingAt);
    if (opportunityName?.trim()) links.names.push(opportunityName.trim());
    linksByIntegration.set(integration, links);
  }
  if (linksByIntegration.size === 0) return null;

  return (
    <div className={classes.crmLinks}>
      {[...linksByIntegration].map(([integration, summary]) => (
        <AlineTooltip
          key={integration}
          arrow
          placement="top"
          title={getCrmLinkTooltip(integration, summary)}
        >
          <span
            className={cx(classes.crmLink, summary.missing && classes.crmLinkMissing)}
            data-cy={summary.missing ? `crm-link-missing-${integration}` : undefined}
          >
            <CrmIntegrationIcon className={classes.crmLinkIcon} integration={integration} />
          </span>
        </AlineTooltip>
      ))}
    </div>
  );
}

/** Falls back to the record type when no name has been read from the CRM yet. */
function getCrmLinkTooltip(
  integration: Apollo.Integration,
  { count, missing, names }: CrmLinkSummary
) {
  const provider = INTEGRATION_DISPLAY_NAME[integration];
  const { lower, plural } = getCrmRecordCopy(integration);
  const article = /^[aeiou]/i.test(provider) ? 'an' : 'a';
  const subject = names.length ? `"${names.join(', ')}"` : `The linked ${provider} ${lower}`;

  return missing
    ? `${subject} no longer exists in ${provider}. Open the document to unlink or relink.`
    : names.length
      ? `Linked to ${names.join(', ')}`
      : count > 1
        ? `Linked to ${count} ${provider} ${plural}`
        : `Linked to ${article} ${provider} ${lower}`;
}
